import { ActionIcon, Button, Divider, Loader, Tooltip } from '@mantine/core';
import { useToggle } from '@mantine/hooks';
import { CircleCheckBig, Clock, Trash2, User } from 'lucide-react';
import { FC, useMemo } from 'react';

import Icon from '@/components/Icon.tsx';
import TaskCreator from '@/components/TaskView/TaskDetails/TaskCreator.tsx';
import TaskPatient from '@/components/TaskView/TaskDetails/TaskPatient.tsx';
import TaskPills from '@/components/TaskView/TaskDetails/TaskPills.tsx';
import Heading2 from '@/components/text/Heading2.tsx';

import { usePvsUserRoles } from '@/hooks/usePvsUserRoles.ts';
import useTask from '@/hooks/useTask.ts';

import { DCTaskStatus } from '@/models/doc-cirrus/DCTask.ts';

import { useTaskViewStore } from '@/stores/taskViewStore.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';
import { cn } from '@/utils/styleUtils.ts';

import Paragraph from '../../text/Paragraph.tsx';

interface Props {
    isCtaAssignToMeLoading: boolean;
    isCtaMarkAsDoneLoading: boolean;
    isCtaDeleteLoading: boolean;
    onAssignToMe: () => void;
    onMarkAsDone: () => void;
    onDelete: () => void;
}

const TaskDetails: FC<Props> = ({
    isCtaAssignToMeLoading,
    isCtaMarkAsDoneLoading,
    isCtaDeleteLoading,
    onAssignToMe,
    onMarkAsDone,
    onDelete,
}) => {
    const [show_delete, toggleShowDelete] = useToggle([false, true]);
    const selected_task_id = useTaskViewStore((state) => state.selected_task_id);
    const selected_task_instance_id = useTaskViewStore((state) => state.selected_task_instance_id);
    const { task: task, is_loading: is_loading_task } = useTask({
        task_id: selected_task_id,
        instance_id: selected_task_instance_id,
    });

    const { pvs_user_roles } = usePvsUserRoles();
    const pvs_user_id = useMemo(
        () => pvs_user_roles.find((role) => role.instance_id === task?.instance_id)?.pvs_user_id,
        [pvs_user_roles, task]
    );

    const disabled_assign_to_me =
        (task?.status === DCTaskStatus.ASSIGNED && task?.employee_id === pvs_user_id) ||
        task?.status === DCTaskStatus.DONE;

    // TODO: Add logic to disable if task is done or not assignable to me

    if (is_loading_task) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (!task) {
        return (
            <div className="flex h-full items-center justify-center">
                <Paragraph>Aufgabe nicht gefunden</Paragraph>
            </div>
        );
    }

    return (
        <>
            <div className="flex justify-between">
                <div className="flex flex-col gap-4">
                    <Heading2 className="text-lg font-bold">{task.title}</Heading2>
                    <TaskPills task={task} />
                </div>
                <Tooltip label="Aufgabe löschen">
                    <ActionIcon
                        onClick={() => toggleShowDelete()}
                        className="bg-transparent text-red-600 hover:bg-transparent hover:text-red-500"
                    >
                        <Icon icon={Trash2} />
                    </ActionIcon>
                </Tooltip>
            </div>
            {show_delete && (
                <>
                    <Divider />
                    <Paragraph>Aufgabe löschen?</Paragraph>
                    <div className="flex gap-2">
                        <Button
                            className="text-white hover:bg-red-600 hover:text-white"
                            color="red"
                            onClick={onDelete}
                            loading={isCtaDeleteLoading}
                        >
                            Löschen
                        </Button>
                        <Button variant="outline" onClick={() => toggleShowDelete()}>
                            Abbrechen
                        </Button>
                    </div>
                </>
            )}
            <Divider />
            <Paragraph className="whitespace-pre-line text-base">{task.details}</Paragraph>
            <div className="flex justify-between">
                {task.alert_time && (
                    <div className="flex-1">
                        <Paragraph>Zieldatum</Paragraph>
                        <div className="flex items-center gap-2">
                            <Clock className="size-4 text-slate-500" />
                            <Paragraph>{dayjs(task.alert_time).format('DD.MM.YYYY')}</Paragraph>
                        </div>
                    </div>
                )}
                <div className="flex-1">
                    <Paragraph>Bearbeiter</Paragraph>
                    <div className="flex items-center gap-2">
                        <User className="size-4 text-slate-500" />
                        <Paragraph>{task.employee_name}</Paragraph>
                    </div>
                </div>
            </div>
            {task.patient_id && <TaskPatient patientId={task.patient_id} instanceId={task.instance_id} />}
            <TaskCreator
                className="mt-4"
                creator={
                    task.creator ? `${task.creator.name.last_name} ${task.creator.name.first_name}` : task.creator_id
                }
                createdAt={task.meta.created_at}
            />
            <Divider />
            <div className="flex justify-between">
                <Tooltip label="Task ist bereits erledigt" disabled={disabled_assign_to_me}>
                    <Button
                        loading={isCtaAssignToMeLoading}
                        className={cn('bg-black', {
                            'bg-gray-300': isCtaMarkAsDoneLoading || disabled_assign_to_me,
                        })}
                        classNames={{ label: 'text-white' }}
                        onClick={onAssignToMe}
                        disabled={isCtaMarkAsDoneLoading || disabled_assign_to_me}
                    >
                        Mir zuweisen
                    </Button>
                </Tooltip>
                <Tooltip label="Task ist bereits erledigt" disabled={task.status !== DCTaskStatus.DONE}>
                    <Button
                        loading={isCtaMarkAsDoneLoading}
                        onClick={onMarkAsDone}
                        leftSection={<CircleCheckBig className="size-5" />}
                        disabled={isCtaAssignToMeLoading || task.status === DCTaskStatus.DONE}
                    >
                        Als erledigt markieren
                    </Button>
                </Tooltip>
            </div>
        </>
    );
};

export default TaskDetails;
